/**
 * WHERE YOU HAVE BEEN, AND WHAT IS LEFT OF IT — the rows behind the orientation pill.
 *
 * Theirs puts a full-screen list behind that pill (universe-v8.dc.html:216): every region of the map
 * with how much of it the reader has walked, "return" on the ones they know and "look from here" on
 * the ones they do not. Its own line says it best: *return anywhere you have walked; places you have
 * not are shown from a distance*.
 *
 * On a map whose whole premise is that the dark is what you have not surveyed, that is the one screen
 * that answers "so where HAVE I been". Ours had the pill in the same place and it did nothing.
 *
 * OURS ARE DERIVED WHERE THEIRS ARE CURATED, and that is the whole difference. Their nodes carry a
 * `region` field and a fixed order to list them in. Ours carry no such thing, so a region here is one
 * of the neighbourhoods `zonesFor` already finds in the tie graph — the same ones the opening flight
 * visits — and its extent is the seed plus everything one step from it. That means a name can fall in
 * two regions, which a curated field would not allow; it is counted in both, because it genuinely is
 * in both and pretending otherwise would need a rule nobody has decided.
 */

/** A neighbourhood of the map, as `zonesFor` reports it. */
export interface RegionSeed {
  id: string;
  name: string;
}

/** One row of the travel list. */
export interface Region {
  id: string;
  name: string;
  /** Names opened here, and names here at all. */
  walked: number;
  total: number;
  /** `walked / total`, and ZERO rather than NaN for a region with nothing in it. */
  share: number;
  /** Whether the reader has been here — which decides what taking the row does. */
  known: boolean;
}

/**
 * The travel rows, in the order the zones were found.
 *
 * `reaches` answers a seed's neighbours; `visited` is what the reader has opened. Both are passed in
 * because this is arithmetic over the map and should be testable without one.
 */
export function regionsFrom(
  seeds: readonly RegionSeed[],
  reaches: (id: string) => readonly string[],
  visited: ReadonlySet<string>,
): Region[] {
  return seeds.map((seed) => {
    /* The seed counts as part of its own region — a reader standing on it has been here. */
    const here = new Set<string>([seed.id, ...reaches(seed.id)]);
    let walked = 0;
    for (const id of here) if (visited.has(id)) walked += 1;
    const total = here.size;
    return {
      id: seed.id,
      name: seed.name,
      walked,
      total,
      share: total === 0 ? 0 : walked / total,
      known: walked > 0,
    };
  });
}
